import clsx from 'clsx';
import { useMemo, type ForwardedRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { ActionRow, ListboxProps } from '../types';

import { typedForwardRef } from '../../../utils/typedForwardRef';
import { typedMemo } from '../../../utils/typedMemo';
import { Layout } from '../../Layout/Layout';
import { Skeleton } from '../../Skeleton/Skeleton';
import { Text } from '../../Text/Text';
import './Listbox.css';
import { MenuRow } from './MenuRow';
import { Option } from './Option';

const messages = defineMessages({
  noResults: {
    id: 'eds.select.noResults',
    defaultMessage: 'No results',
    description: 'Empty state when Select filter matches no options',
  },
});

const LOADING_PLACEHOLDER_ROW_COUNT = 3;

const NO_ROWS: ActionRow[] = [];

export const Listbox = typedMemo(
  typedForwardRef(function Listbox<V = string>(
    {
      activeIndex,
      footerRows,
      groups,
      isKeyboardModality,
      isOptionSelected,
      onHoverIndex,
      onSelectOption,
      isEmpty,
      isLoading,
      isLoadingOptions,
      label,
      multiple,
      noResultsText,
      __private,
    }: ListboxProps<V>,
    forwardedRef: ForwardedRef<HTMLDivElement>,
  ) {
    const {
      ariaLabelledBy,
      disabled,
      elementsRef,
      floatingProps,
      id,
      inline,
      leadingRows = NO_ROWS,
      style,
      tabIndex,
    } = __private;
    const intl = useIntl();

    const leadingCount = leadingRows.length;

    /** Flat index each group's first option sits at. */
    const groupOffsets = useMemo(() => {
      let offset = leadingCount;
      return groups.map((group) => {
        const start = offset;
        offset += group.options.length;
        return start;
      });
    }, [groups, leadingCount]);
    const optionCount = groups.reduce(
      (count, group) => count + group.options.length,
      0,
    );

    elementsRef.current.length = leadingCount + optionCount + footerRows.length;

    return (
      <div
        {...floatingProps}
        aria-busy={isLoading || undefined}
        aria-label={label}
        aria-labelledby={ariaLabelledBy}
        aria-multiselectable={multiple || undefined}
        className={clsx(
          'eds-select--listbox',
          inline && 'eds-select--listbox--inline',
          disabled && 'eds-select--listbox--disabled',
        )}
        id={id}
        ref={forwardedRef}
        role="listbox"
        style={style}
        {...(tabIndex === undefined ? {} : { tabIndex })}
      >
        {leadingRows.map((row, rowIndex) => (
          <MenuRow
            {...row}
            index={rowIndex}
            key={row.id}
            __private={{
              elementsRef,
              isKeyboardFocused: isKeyboardModality && activeIndex === rowIndex,
              onHoverIndex,
            }}
          />
        ))}
        {isLoadingOptions ? (
          <Layout
            aria-hidden
            className="eds-select--loading"
            direction="column"
            spacing="m"
          >
            {Array.from({
              length: LOADING_PLACEHOLDER_ROW_COUNT,
            }).map((_, row) => (
              <Skeleton key={row} size="m" />
            ))}
          </Layout>
        ) : null}
        {isEmpty ? (
          <div className="eds-select--empty">
            <Text tone="subdued" variant="body-m">
              {noResultsText ?? intl.formatMessage(messages.noResults)}
            </Text>
          </div>
        ) : null}
        {groups.map((group, groupIndex) => {
          const options = group.options.map((option, optionIndex) => {
            // Flat position drives navigation, so it counts from the
            // start of the whole list rather than from the group.
            const index = groupOffsets[groupIndex] + optionIndex;
            const active = activeIndex === index;

            return (
              <Option
                active={active}
                id={`${id}-option-${option.id}`}
                index={index}
                key={option.id}
                option={option}
                selected={isOptionSelected(option.value)}
                onSelect={onSelectOption}
                __private={{
                  elementsRef,
                  isKeyboardFocused: isKeyboardModality && active,
                  onHoverIndex,
                }}
              />
            );
          });

          if (!group.text) return options;

          const groupLabelId = `${id}-group-${group.id}`;

          return (
            <div aria-labelledby={groupLabelId} key={group.id} role="group">
              <div className="eds-select--group-label" id={groupLabelId}>
                <Text tone="subdued" truncate={1} variant="overline">
                  {group.text}
                </Text>
              </div>
              {options}
            </div>
          );
        })}
        {footerRows.length > 0 ? (
          <div className="eds-select--footer" role="none">
            {footerRows.map((row, rowIndex) => {
              const index = leadingCount + optionCount + rowIndex;

              return (
                <MenuRow
                  {...row}
                  index={index}
                  key={row.id}
                  __private={{
                    elementsRef,
                    isKeyboardFocused:
                      isKeyboardModality && activeIndex === index,
                    onHoverIndex,
                  }}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }),
);

Listbox.displayName = 'Listbox';
